// Counts the number of digits of a number (e.g., 10 -> 2 digits, 999 -> 3 digits)
export const numDigits = (x: number): number =>
  Math.max(Math.floor(Math.log10(Math.abs(x))), 0) + 1;

/**
 * Returns a number with a maximum precision; trailing zeros will be truncated
 *
 * @param n Input number for the basis of the precision adjustment
 * @param precision Maximum precision of the number right of the decimal; must be greater
 * than zero
 */
export const maxPrecision = (n: number, precision: number): number =>
  Math.floor(n * Math.pow(10, precision)) / Math.pow(10, precision);

/**
 * Checks if a value is a finite number
 * @param value The value to check
 * @returns true if the value is a finite number, false otherwise
 */
export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Clamps a number between a minimum and maximum value
 * @param value The number to clamp
 * @param min The minimum value to clamp to
 * @param max The maximum value to clamp to
 * @returns The clamped number
 */
export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 *  Returns the value if it's a positive finite number, otherwise returns the fallback
 * @param value The value to check
 * @param fallback The fallback value to return if the input value is not a positive finite number
 * @returns The input value if it's a positive finite number, otherwise the fallback value
 */
export const positiveOr = (value: unknown, fallback: number): number =>
  isFiniteNumber(value) && value > 0 ? value : fallback;
